import { Calendar, AlertCircle, Activity, BarChart2, Heart } from 'lucide-react';
import GlassCard from '../ui/glass-card';

export interface Condition {
  id: string;
  name: string;
  diagnosisDate: string;
  status: 'active' | 'resolved' | 'remission' | 'chronic';
  severity: 'mild' | 'moderate' | 'severe';
  description: string;
  symptoms: string[];
  treatments: Array<{
    id: string;
    name: string;
    type: 'medication' | 'procedure' | 'therapy' | 'lifestyle';
    dosage?: string;
    frequency?: string;
    startDate: string;
    endDate?: string;
    notes?: string;
  }>;
  diagnostics?: Array<{
    id: string;
    name: string;
    date: string;
    result: string;
    attachmentUrl?: string;
  }>;
}

interface ConditionCardProps {
  condition: Condition;
  className?: string;
  onSelect?: (condition: Condition) => void;
}

/**
 * Component for displaying a medical condition with details in a card format
 */
const ConditionCard: React.FC<ConditionCardProps> = ({
  condition,
  className = '',
  onSelect,
}) => {
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[var(--status-error-subtle)] text-[var(--status-error)]';
      case 'resolved':
        return 'bg-[var(--status-success-subtle)] text-[var(--status-success)]';
      case 'remission':
        return 'bg-[var(--status-warning-subtle)] text-[var(--status-warning)]';
      case 'chronic':
        return 'bg-[var(--clinical-heart-subtle)] text-[var(--clinical-heart)]';
      default:
        return 'bg-[var(--background-subtle)] text-[var(--text-secondary)]';
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'severe':
        return <AlertCircle className="w-4 h-4" />;
      case 'moderate':
        return <Activity className="w-4 h-4" />;
      case 'mild':
        return <BarChart2 className="w-4 h-4" />;
      default:
        return <BarChart2 className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleClick = () => {
    if (onSelect) {
      onSelect(condition);
    }
  };

  return (
    <GlassCard 
      variant="default"
      padding="none"
      className={`overflow-hidden border transition-all duration-300 ${className} ${onSelect ? 'cursor-pointer hover:shadow-lg hover:border-[var(--brand-primary)]/30 group' : ''}`}
      onClick={handleClick}
    >
      {/* Header with condition name and status */}
      <div className="p-4 border-b border-[var(--border-subtle)]">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">{condition.name}</h3>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(condition.status)}`}>
            {condition.status}
          </div>
        </div>
        <div className="mt-2 flex items-center text-[var(--text-secondary)] text-sm">
          <Calendar className="w-4 h-4 mr-1" />
          <span>Diagnosed: {formatDate(condition.diagnosisDate)}</span>
        </div>
      </div>

      {/* Description and details */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1 bg-[var(--background-subtle)] px-2 py-1 rounded-md text-xs font-medium text-[var(--text-secondary)]">
            {getSeverityIcon(condition.severity)}
            <span className="capitalize">{condition.severity}</span>
          </div>
        </div>

        <p className="text-[var(--text-secondary)] text-sm mb-4">{condition.description}</p>

        {/* Symptoms */}
        {condition.symptoms && condition.symptoms.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">Symptoms</h4>
            <div className="flex flex-wrap gap-2">
              {condition.symptoms.map((symptom, index) => (
                <span 
                  key={index} 
                  className="bg-[var(--background-subtle)] text-[var(--text-secondary)] text-xs px-2 py-1 rounded-full"
                >
                  {symptom}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Treatments preview */}
        {condition.treatments && condition.treatments.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">Current Treatments</h4>
            <ul className="space-y-2">
              {condition.treatments.slice(0, 2).map((treatment) => (
                <li 
                  key={treatment.id} 
                  className="flex items-start gap-2 p-2 bg-[var(--background-subtle)] rounded-md"
                >
                  <div className="mt-0.5">
                    {treatment.type === 'medication' && (
                      <Heart className="w-4 h-4 text-[var(--clinical-heart)]" />
                    )}
                    {treatment.type === 'procedure' && (
                      <Activity className="w-4 h-4 text-[var(--clinical-glucose)]" />
                    )}
                    {treatment.type === 'therapy' && (
                      <Activity className="w-4 h-4 text-[var(--clinical-blood)]" />
                    )}
                    {treatment.type === 'lifestyle' && (
                      <BarChart2 className="w-4 h-4 text-[var(--clinical-liver)]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[var(--text-primary)]">{treatment.name}</p>
                    {treatment.dosage && (
                      <p className="text-xs text-[var(--text-secondary)]">
                        {treatment.dosage} {treatment.frequency && `• ${treatment.frequency}`}
                      </p>
                    )}
                  </div>
                </li>
              ))}

              {condition.treatments.length > 2 && (
                <li className="text-center text-xs text-[var(--brand-primary)] font-medium">
                  + {condition.treatments.length - 2} more treatments
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Footer with action button if onSelect provided */}
      {onSelect && (
        <div className="px-4 py-3 bg-[var(--background-subtle)] text-right">
          <button 
            className="text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)]"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(condition);
            }}
          >
            View Complete History
          </button>
        </div>
      )}
    </GlassCard>
  );
};

export default ConditionCard; 